// Shared bits of the git remote pages: api types, human labels, the write-only text areas for keys and
// certificates, and the step-by-step result of a connection test (with trust-on-first-use of a host key).
import type { paths } from '@cortex/api-client';
import { AlertTriangle, CheckCircle2, CircleSlash, KeyRound, Loader2, XCircle } from 'lucide-react';
import * as React from 'react';
import { CopyButton } from '../../workspace/index.ts';

type Json<P extends keyof paths, M extends 'get' | 'post' | 'put' | 'patch'> = paths[P][M] extends {
  responses: { 200: { content: { 'application/json': infer T } } };
}
  ? T
  : paths[P][M] extends { responses: { 201: { content: { 'application/json': infer T } } } }
    ? T
    : never;

export type Providers = Json<'/api/git-remotes/providers', 'get'>;
export type Preset = Providers['providers'][number];
export type ConnectionList = Json<'/api/git-remotes/orgs/{org}/connections', 'get'>;
export type Connection = ConnectionList['connections'][number];
export type ProbeReport = Json<'/api/git-remotes/orgs/{org}/connections/{id}/test', 'post'>;
export type Repository = Json<'/api/git-remotes/projects/{slug}/repository', 'get'>;
export type Compare = Json<'/api/git-remotes/projects/{slug}/repository/compare', 'post'>;
export type ProviderId = Preset['id'];
export type AuthKind = Preset['authKinds'][number];

export const BTN =
  'inline-flex items-center gap-1.5 h-8 px-3 rounded-[5px] border border-page-divider text-sm cursor-pointer bg-card-bg text-page-text hover:border-nav-active disabled:opacity-50';
export const PRIMARY =
  'inline-flex items-center gap-1.5 h-8 px-3 rounded-[5px] bg-nav-active text-white text-sm font-medium cursor-pointer hover:bg-nav-active-dark border-0 disabled:opacity-50';
export const INPUT =
  'w-full h-9 px-3 rounded-[5px] border border-page-divider bg-page-bg text-page-text text-sm outline-none focus:border-nav-active';
export const AREA =
  'w-full min-h-[84px] px-3 py-2 rounded-[5px] border border-page-divider bg-page-bg text-page-text text-xs font-mono outline-none focus:border-nav-active';
export const LABEL = 'text-xs text-page-muted';

/** Human names of the auth kinds, per provider where the host has its own word for it. */
export function authLabel(kind: AuthKind | null | undefined, provider?: ProviderId | null): string {
  switch (kind) {
    case 'token':
      return provider === 'github' || provider === 'github_enterprise'
        ? 'Personal access token (fine-grained or classic)'
        : provider === 'gitlab' || provider === 'gitlab_self_managed'
          ? 'Project, group or personal access token'
          : provider === 'bitbucket_cloud'
            ? 'Access token (repository, project or workspace)'
            : provider === 'bitbucket_datacenter'
              ? 'HTTP access token'
              : provider?.startsWith('azure')
                ? 'Personal access token'
                : 'Access token';
    case 'app_password':
      return 'User name and app password';
    case 'github_app':
      return 'GitHub App';
    case 'oauth_app':
      return 'OAuth app';
    case 'basic':
      return provider === 'codecommit' ? 'HTTPS Git credentials (IAM user)' : 'User name and password';
    case 'ssh_generated':
      return 'SSH deploy key generated by cortex';
    case 'ssh_uploaded':
      return 'SSH key you upload';
    case 'none':
      return 'No credential';
    default:
      return '—';
  }
}

export const MODE_LABEL: Record<Repository['mode'], string> = {
  connection: 'Through an organization connection',
  default: 'Through the organization’s default connection',
  installation: 'Through the installation’s default (set by the operator)',
  url: 'A linked repository URL',
  local: 'Local only: nothing is pushed or fetched',
  none: 'No remote: the organization has no default connection',
};

export const when = (iso: string | null | undefined) => (iso ? new Date(iso).toLocaleString() : 'never');

/** A multi-line write-only secret (a private key): "saved" once held, replaceable, never shown back. */
export function SecretArea({
  saved,
  value,
  onChange,
  testId,
  placeholder,
  label,
}: {
  saved: boolean;
  value: string | undefined;
  onChange: (v: string | undefined) => void;
  testId: string;
  placeholder?: string;
  label: string;
}) {
  if (saved && value === undefined)
    return (
      <div className="flex items-center gap-3" data-testid={`${testId}-saved`}>
        <span className="inline-flex items-center gap-2 h-9 px-3 rounded-[5px] border border-page-divider bg-page-bg text-page-muted text-sm flex-1">
          <span aria-hidden className="tracking-widest">
            ••••••••
          </span>
          <span className="text-xs">Saved, held encrypted</span>
        </span>
        <button
          type="button"
          className="bg-transparent border-0 p-0 text-xs font-medium text-nav-active cursor-pointer hover:underline"
          onClick={() => onChange('')}
          data-testid={`${testId}-replace`}
        >
          Replace
        </button>
      </div>
    );
  return (
    <textarea
      className={AREA}
      value={value ?? ''}
      aria-label={label}
      placeholder={placeholder}
      spellCheck={false}
      autoComplete="off"
      onChange={(e) => onChange(e.target.value)}
      data-testid={testId}
    />
  );
}

/** The public half of a key, to paste into the host. */
export function PublicKey({
  value,
  fingerprint,
  help,
}: {
  value: string;
  fingerprint?: string | null;
  help?: string;
}) {
  return (
    <div
      className="rounded-[6px] border border-page-divider bg-page-bg p-3 grid gap-1.5"
      data-testid="public-key"
    >
      <div className="text-xs text-page-text flex items-center gap-1.5">
        <KeyRound size={13} /> Public key — add it to the git host{fingerprint ? ` (${fingerprint})` : ''}
      </div>
      <div className="flex items-start gap-2">
        <code
          className="flex-1 font-mono text-[11px] break-all text-page-text"
          data-testid="public-key-value"
        >
          {value}
        </code>
        <CopyButton value={value} testId="public-key-copy" />
      </div>
      {help && <div className="text-[11px] text-page-muted">{help}</div>}
    </div>
  );
}

const ICON = {
  ok: <CheckCircle2 size={15} className="text-emerald-600 shrink-0" />,
  failed: <XCircle size={15} className="text-red-600 shrink-0" />,
  skipped: <CircleSlash size={15} className="text-page-muted shrink-0" />,
  warning: <AlertTriangle size={15} className="text-amber-600 shrink-0" />,
  needs_trust: <KeyRound size={15} className="text-amber-600 shrink-0" />,
};

/** A connection test, step by step; a host key to trust offers the button that pins it. */
export function ProbeResult({
  report,
  onTrust,
  busy,
}: {
  report: ProbeReport;
  onTrust?: (lines: string[]) => void;
  busy?: boolean;
}) {
  return (
    <div className="grid gap-1.5" data-testid="probe-result" data-ok={report.ok}>
      <div className={`text-sm font-medium ${report.ok ? 'text-emerald-700' : 'text-red-700'}`}>
        {report.ok ? 'The connection works' : 'The connection does not work yet'}{' '}
        <span className="text-xs text-page-muted font-normal font-mono">{report.target}</span>
      </div>
      {report.steps.map((s) => (
        <div
          key={s.key}
          className="flex items-start gap-2 text-sm"
          data-testid={`probe-${s.key}`}
          data-status={s.status}
        >
          {ICON[s.status]}
          <div className="min-w-0">
            <span className="text-page-text">{s.label}</span>
            <span className="text-page-muted"> — {s.detail}</span>
            {s.ms > 0 && <span className="text-[11px] text-page-muted"> · {s.ms} ms</span>}
            {s.hostKeys && s.hostKeys.length > 0 && (
              <div className="mt-1.5 grid gap-1">
                {s.hostKeys.map((k) => (
                  <div key={k.line} className="flex items-center gap-2 text-xs">
                    <code className="font-mono text-page-text">
                      {k.type} {k.fingerprint}
                    </code>
                  </div>
                ))}
                {onTrust && s.status === 'needs_trust' && (
                  <button
                    type="button"
                    className={BTN + ' w-fit'}
                    disabled={busy}
                    onClick={() => onTrust(s.hostKeys!.map((k) => k.line))}
                    data-testid="trust-host-key"
                  >
                    {busy ? <Loader2 size={13} className="animate-spin" /> : <KeyRound size={13} />} I checked
                    the fingerprint: trust this host
                  </button>
                )}
              </div>
            )}
          </div>
        </div>
      ))}
    </div>
  );
}
